import {useState, useEffect, useRef} from "react";
import Hls from "hls.js";

const formatTime = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${hours > 0 ? hours + "h " : ""}${minutes > 0 ? minutes + "m " : ""}${seconds}s`;
};

const StatBox = ({label, value, sub, color}) => (
  <div className="bg-slate-300/50 border border-slate-300 p-5 rounded-2xl flex-1">
    <p className="text-slate-500 text-xs font-bold uppercase tracking-widest">
      {label}
    </p>
    <div className="flex items-baseline gap-2 mt-1">
      <span className={`text-2xl font-mono font-bold ${color}`}>{value}</span>
      <span className="text-slate-600 text-xs">{sub}</span>
    </div>
  </div>
);

const VideoCard = ({video, savedTime, onProgress, isActive, onPlay}) => {
  const videoRef = useRef(null);
  const ytContainerRef = useRef(null);
  const playerRef = useRef(null);
  const lastTimeRef = useRef(savedTime);

  const isYouTube =
    video.url.includes("youtube.com") || video.url.includes("youtu.be");

  useEffect(() => {
    if (!isActive) {
      if (isYouTube && playerRef.current?.pauseVideo)
        playerRef.current.pauseVideo();
      else if (videoRef.current) videoRef.current.pause();
    }
  }, [isActive, isYouTube]);

  useEffect(() => {
    if (isYouTube) {
      if (!window.YT) {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        const firstScriptTag = document.getElementsByTagName("script")[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      }

      const videoId = video.url.includes("v=")
        ? video.url.split("v=")[1].split("&")[0]
        : video.url.split("/").pop().split("?")[0];

      const initYT = () => {
        if (!ytContainerRef.current) return;
        playerRef.current = new window.YT.Player(ytContainerRef.current, {
          height: "100%",
          width: "100%",
          videoId: videoId,
          playerVars: {autoplay: 0, rel: 0},
          events: {
            onReady: (e) => {
              if (savedTime > 0) e.target.seekTo(savedTime, false);
            },
            onStateChange: (event) => {
              if (event.data === window.YT.PlayerState.PLAYING) {
                onPlay(video.id);
                const interval = setInterval(() => {
                  if (playerRef.current?.getPlayerState?.() === 1) {
                    const currentTime = playerRef.current.getCurrentTime();
                    const delta = currentTime - lastTimeRef.current;
                    if (delta > 0 && delta < 2)
                      onProgress(video.id, currentTime, delta);
                    lastTimeRef.current = currentTime;
                  } else clearInterval(interval);
                }, 1000);
              }
            },
          },
        });
      };

      if (window.YT && window.YT.Player) initYT();
      else {
        const checkReady = setInterval(() => {
          if (window.YT && window.YT.Player) {
            initYT();
            clearInterval(checkReady);
          }
        }, 100);
        return () => clearInterval(checkReady);
      }
      return () => playerRef.current?.destroy();
    } else {
      const videoElement = videoRef.current;
      if (!videoElement) return;
      if (video.url.includes(".m3u8")) {
        if (Hls.isSupported()) {
          const hls = new Hls();
          hls.loadSource(video.url);
          hls.attachMedia(videoElement);
        } else if (videoElement.canPlayType("application/vnd.apple.mpegurl")) {
          videoElement.src = video.url;
        }
      } else {
        videoElement.src = video.url;
      }
    }
  }, [video.id, video.url, isYouTube]);

  const handleTimeUpdate = (e) => {
    if (isYouTube) return;
    const currentTime = e.target.currentTime;
    const delta = currentTime - lastTimeRef.current;
    if (delta > 0 && delta < 1) onProgress(video.id, currentTime, delta);
    lastTimeRef.current = currentTime;
  };

  return (
    <div className="group rounded-2xl border border-slate-800 overflow-hidden bg-slate-300/50 hover:border-slate-600 transition-all flex flex-col h-full">
      <div className="w-full aspect-video bg-black relative">
        {isYouTube ? (
          <div ref={ytContainerRef} className="absolute inset-0" />
        ) : (
          <video
            ref={videoRef}
            controls
            autoPlay={false}
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover"
            onPlay={() => onPlay(video.id)}
            onLoadedMetadata={(e) => {
              e.target.currentTime = savedTime;
            }}
            onTimeUpdate={handleTimeUpdate}
          />
        )}
      </div>
      <div className="p-4 flex flex-col flex-grow justify-between">
        <div>
          <h3 className="font-semibold text-slate-500 truncate">
            {video.title}
          </h3>
          <p className="text-slate-400 text-xs uppercase tracking-widest">
            {video.category}
          </p>
        </div>
      </div>
    </div>
  );
};

const VideoDashboard = ({user}) => {
  const [videos, setVideos] = useState([]);
  const [watchHistory, setWatchHistory] = useState({});
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [activity, setActivity] = useState({
    mouseMoves: 0,
    clicks: 0,
    secondsActive: 0,
    totalVideoSeconds: 0,
  });

  const activityRef = useRef(activity);
  const historyRef = useRef(watchHistory);

  useEffect(() => {
    activityRef.current = activity;
  }, [activity]);

  useEffect(() => {
    historyRef.current = watchHistory;
  }, [watchHistory]);

  const categories = ["All", ...new Set(videos.map((v) => v.category))];

  // API Call to fetch videos on page load
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_AWS_USER_ENDPOINT}/get-videos`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${process.env.REACT_APP_AWS_USER_TOKEN}`,
            },
          },
        );
        const data = await response.json();
        if (data.result === "success") {
          data.data.sort((a, b) => a.id.localeCompare(b.id));
          setVideos(data.data);
        }
      } catch (err) {
        console.error("Failed to fetch videos", err);
      }
    };

    fetchVideos();
  }, []);

  // API Call to fetch initial stats on page load
  useEffect(() => {
    const fetchInitialStats = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_AWS_USER_ENDPOINT}/get-contact/id/${user.signInDetails.loginId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${process.env.REACT_APP_AWS_USER_TOKEN}`,
            },
          },
        );
        const data = await response.json();
        if (data.result === "success" && data.data.length > 0) {
          if (data.data[0].activity) setActivity(data.data[0].activity);
          if (data.data[0].watch_history)
            setWatchHistory(data.data[0].watch_history);
        }
      } catch (err) {
        console.error("Failed to fetch initial stats", err);
      }
    };

    if (user?.signInDetails?.loginId) {
      fetchInitialStats();
    }
  }, [user]);

  // API Sync Logic: Sends data to backend every 30 seconds
  useEffect(() => {
    const syncWithBackend = async () => {
      try {
        await fetch(
          `${process.env.REACT_APP_AWS_USER_ENDPOINT}/update-contact/activity`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${process.env.REACT_APP_AWS_USER_TOKEN}`,
            },
            body: JSON.stringify({
              customer_id_u: user?.signInDetails?.loginId,
              customer_firstname_u: user?.given_name || "Unknown",
              customer_lastname_u: user?.family_name || "Unknown",
              activity_u: activityRef.current,
              watch_history_u: historyRef.current,
              timestamp_u: new Date().toISOString(),
            }),
          },
        );
        console.log("Backend sync successful");
      } catch (err) {
        console.error("Backend sync failed", err);
      }
    };

    const backendTimer = setInterval(syncWithBackend, 30000);
    return () => clearInterval(backendTimer);
  }, [user]);

  useEffect(() => {
    const handleMouseMove = () =>
      setActivity((prev) => ({...prev, mouseMoves: prev.mouseMoves + 1}));
    const handleClick = () =>
      setActivity((prev) => ({...prev, clicks: prev.clicks + 1}));

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("click", handleClick);

    const timer = setInterval(() => {
      if (!document.hidden)
        setActivity((prev) => ({
          ...prev,
          secondsActive: prev.secondsActive + 1,
        }));
    }, 1000);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("click", handleClick);
      clearInterval(timer);
    };
  }, []);

  const handleVideoProgress = (id, currentTime, delta) => {
    setWatchHistory((prev) => ({
      ...prev,
      [id]: currentTime,
    }));

    if (delta) {
      setActivity((prev) => ({
        ...prev,
        totalVideoSeconds: prev.totalVideoSeconds + delta,
      }));
    }
  };

  const filteredVideos = videos.filter((v) => {
    const matchesSearch = v.title
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === "All" || v.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="h-full w-full p-10 font-sans">
      <header className="mb-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          <StatBox
            label="Interactions"
            value={activity.mouseMoves.toLocaleString()}
            sub="Events"
            color="text-purple-400"
          />
          <StatBox
            label="Total Clicks"
            value={activity.clicks}
            sub="Clicks"
            color="text-pink-400"
          />
          <StatBox
            label="Session Time"
            value={formatTime(activity.secondsActive)}
            sub="Elapsed"
            color="text-emerald-400"
          />
          <StatBox
            label="Video Playtime"
            value={formatTime(activity.totalVideoSeconds)}
            sub="Cumulative"
            color="text-orange-400"
          />
        </div>
        <div className="flex flex-col md:flex-row gap-4 p-4 bg-slate-300/50 rounded-2xl border border-slate-300">
          <input
            type="text"
            placeholder="Search by title..."
            className="flex-grow bg-slate-100 border border-slate-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-orange-500 outline-none transition-all"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <select
            className="bg-slate-100 border border-slate-300 rounded-xl px-4 py-2 cursor-pointer focus:ring-2 focus:ring-orange-500 outline-none"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
      </header>
      <main className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-10">
        {filteredVideos.map((video) => (
          <VideoCard
            key={video.id}
            video={video}
            savedTime={watchHistory[video.id] || 0}
            onProgress={handleVideoProgress}
            isActive={activeVideoId === video.id}
            onPlay={(id) => setActiveVideoId(id)}
          />
        ))}
      </main>
    </div>
  );
};

export default VideoDashboard;
